import React from 'react';
import ArtistSocialLinks, { ARTIST_SOCIAL_LINK_ITEMS } from '../../common/artists/ArtistSocialLinks';

export const BUBBLE_SIZES = {
  small: 50,
  medium: 80,
  large: 100,
  xlarge: 120,
};

export const MIN_SIZE = BUBBLE_SIZES.small;
export const CONNECTION_DISTANCE = 120;
const BASE_COUNT = 30;

export const resolveViewportWidth = () => {
  if (typeof window === 'undefined') return 1200;
  return window.innerWidth || 1200;
};

export const getLayoutConfig = (count, viewportWidth) => {
  const width = viewportWidth || 1200;
  const isSmall = width <= 640;
  const isMedium = width <= 900;
  const baseMin = isSmall ? 520 : isMedium ? 580 : 640;
  const baseMax = isSmall ? 700 : isMedium ? 780 : 880;
  const ratio = isSmall ? 66 : isMedium ? 70 : 76;
  const perItem = isSmall ? 6 : isMedium ? 7 : 8;
  const extraCap = isSmall ? 240 : isMedium ? 300 : 360;
  const extra = Math.min(Math.max(0, count - BASE_COUNT) * perItem, extraCap);
  const maxBubbleSize = Math.max(
    BUBBLE_SIZES.large + 10,
    isSmall ? 110 : isMedium ? 115 : BUBBLE_SIZES.xlarge
  );
  const bubbleSizes = {
    small: MIN_SIZE,
    medium: BUBBLE_SIZES.medium,
    large: BUBBLE_SIZES.large,
    xlarge: maxBubbleSize,
  };
  return {
    minHeight: baseMin + extra,
    maxHeight: baseMax + extra,
    ratio,
    minSize: bubbleSizes.small,
    maxSize: bubbleSizes.xlarge,
    bubbleSizes,
  };
};

const imageCache = new Map();

export const getCachedImage = (url, onLoad) => {
  if (!url) return { image: null, isLoaded: false };
  const cached = imageCache.get(url);
  if (cached) {
    if (onLoad && !cached.loaded && !cached.error) {
      cached.listeners.add(onLoad);
    }
    return { image: cached.image, isLoaded: cached.loaded };
  }
  const image = new Image();
  const entry = {
    image,
    loaded: false,
    error: false,
    listeners: new Set(),
  };
  if (onLoad) entry.listeners.add(onLoad);
  image.addEventListener('load', () => {
    entry.loaded = true;
    entry.listeners.forEach((cb) => cb());
    entry.listeners.clear();
  });
  image.addEventListener('error', () => {
    entry.error = true;
    entry.listeners.clear();
  });
  image.src = url;
  imageCache.set(url, entry);
  return { image, isLoaded: false };
};

export const resolveArtistEmail = (artist) => {
  if (!artist) return '';
  if (artist.is_registered) return artist.email || '';
  return artist.provisional_email || artist.email || '';
};

export const calcAge = (y, m, d) => {
  if (!y) return null;
  const year = parseInt(y, 10);
  if (Number.isNaN(year)) return null;
  const month = m ? parseInt(m, 10) : null;
  const day = d ? parseInt(d, 10) : null;
  const today = new Date();
  let age = today.getFullYear() - year;
  if (month !== null) {
    const mDiff = (today.getMonth() + 1) - month;
    if (mDiff < 0) age -= 1;
    else if (mDiff === 0 && day !== null && today.getDate() < day) age -= 1;
  }
  return age;
};

export const renderGenderJP = (gender) => {
  if (gender === 'male') return '男性';
  if (gender === 'female') return '女性';
  if (gender === 'secret') return '非公表';
  return '';
};

export const firstNonEmpty = (...vals) => vals.find((v) => typeof v === 'string' && v.trim()) || '';

export const getBadgeLabel = (artist, partByValue, groupTagById) => {
  if (!artist) return '';
  if (artist.account_type === 'group') {
    const mainValue = Array.isArray(artist.group_tags) ? artist.group_tags[0] : null;
    if (!mainValue) return '';
    return groupTagById?.get(String(mainValue)) || '';
  }
  const mainValue = Array.isArray(artist.parts) ? artist.parts[0] : null;
  if (!mainValue) return '';
  const part = partByValue?.get(mainValue);
  return part?.label || '';
};

export const SNS_ITEMS = ARTIST_SOCIAL_LINK_ITEMS;

export const SnsIcons = ({ artist, items = SNS_ITEMS }) => (
  <ArtistSocialLinks
    artist={artist}
    items={items}
    size="sm"
    className="artist-bubble-card__sns"
  />
);

export const computeImportance = (index, total) => {
  if (total <= 1) return 1;
  return 1 - (index / (total - 1));
};

export const resolveTierIndex = (importance) => {
  if (importance >= 0.93) return 3;
  if (importance >= 0.78) return 2;
  if (importance >= 0.6) return 1;
  return 0;
};
